import { useState } from "react";
import { motion } from "framer-motion";
import { 
  Heart, 
  Copy,
  Check,
  ExternalLink,
  Sparkles,
  Quote,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const REVOLUT_LINK = "https://revolut.me/autocora"; // Schimbă cu link-ul tău
const REVOLUT_IBAN = "RO00BREL0000000000000000"; // Schimbă cu IBAN-ul tău

const testimonials = [
  {
    text: "Am luat permisul din prima datorită testelor de aici. Merită tot sprijinul!",
    author: "Maria D.",
    location: "București"
  },
  {
    text: "Cel mai bun mod de a te pregăti pentru examen. Donez lunar cu plăcere.",
    author: "Andrei P.",
    location: "Cluj-Napoca"
  },
  {
    text: "Platforma e gratuită și de calitate. E normal să susținem astfel de proiecte.",
    author: "Elena M.",
    location: "Timișoara"
  }
];

export default function DonatePage() {
  const [donationAmount, setDonationAmount] = useState("");
  const [copiedIBAN, setCopiedIBAN] = useState(false);

  const copyIBAN = () => {
    navigator.clipboard.writeText(REVOLUT_IBAN);
    setCopiedIBAN(true);
    toast.success("IBAN copiat!");
    setTimeout(() => setCopiedIBAN(false), 3000);
  };

  const handleDonate = () => {
    window.open(REVOLUT_LINK, "_blank");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="donate-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-rose-600 via-pink-600 to-purple-700 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-8"
            >
              <Heart className="w-10 h-10 text-white" fill="currentColor" />
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6 font-['Archivo']">
              Susține AUTO CORA
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto leading-relaxed">
              Dacă ți-am fost de ajutor, ne poți susține printr-o donație.
            </p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap justify-center gap-4 mt-8"
            >
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                <span>100% voluntar</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Heart className="w-5 h-5 text-red-300" />
                <span>Fără obligații</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f8fafc"/>
          </svg>
        </div>
      </section>

      {/* Donation Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Archivo']">
              Donează cât dorești
            </h2>
            <p className="text-slate-600">
              Orice sumă contează și este apreciată
            </p>
          </motion.div>

          {/* Amount Input */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="border-2 border-slate-200 shadow-xl mb-8">
              <CardContent className="p-8">
                <p className="text-sm font-medium text-slate-600 mb-4 text-center">Introdu suma dorită:</p>
                <div className="relative max-w-xs mx-auto">
                  <input
                    type="number"
                    placeholder="0"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(e.target.value)}
                    className="w-full px-6 py-4 pr-20 text-4xl font-black text-center text-slate-900 border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-rose-500 transition-colors"
                  />
                  <span className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xl">RON</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Button
              size="lg"
              onClick={handleDonate}
              className="h-16 px-12 text-lg font-bold bg-gradient-to-r from-rose-500 to-purple-600 hover:from-rose-600 hover:to-purple-700 shadow-xl shadow-rose-500/30"
            >
              <Heart className="w-5 h-5 mr-2" fill="currentColor" />
              Donează prin Revolut
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-sm text-slate-500 mt-4">
              Vei fi redirecționat către Revolut
            </p>
          </motion.div>

          {/* Alternative Payment - IBAN */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="border-0 shadow-lg bg-slate-50">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Preferi transfer bancar?</h3>
                <p className="text-slate-600 mb-6">Poți dona direct prin transfer în contul nostru Revolut</p>
                
                <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">IBAN</p>
                      <code className="text-lg font-mono font-bold text-slate-900">{REVOLUT_IBAN}</code>
                    </div>
                    <Button
                      variant="outline"
                      onClick={copyIBAN}
                      className="flex-shrink-0"
                    >
                      {copiedIBAN ? (
                        <><Check className="w-4 h-4 mr-2 text-green-500" /> Copiat!</>
                      ) : (
                        <><Copy className="w-4 h-4 mr-2" /> Copiază</>
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 text-sm text-slate-600">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ChevronRight className="w-3 h-3 text-blue-600" />
                  </div>
                  <p>Beneficiar: <strong>Cora Ionut Marius</strong></p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Archivo']">
              Ce spun susținătorii noștri
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
                  <CardContent className="p-6">
                    <Quote className="w-8 h-8 text-rose-200 mb-4" fill="currentColor" />
                    <p className="text-slate-700 mb-6 leading-relaxed">"{testimonial.text}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{testimonial.author}</p>
                        <p className="text-sm text-slate-500">{testimonial.location}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-16 h-16 text-rose-500 mx-auto mb-6" fill="currentColor" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Archivo']">
              Mulțumim pentru susținere!
            </h2>
            <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
              Fiecare contribuție ne ajută să continuăm acest proiect.
            </p>
            <Button
              size="lg"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-white text-slate-900 hover:bg-slate-100 font-bold"
            >
              Donează acum
              <Heart className="w-4 h-4 ml-2 text-rose-500" fill="currentColor" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
